import { formatRelativeTime } from "@/lib/formatRelativeTime";
import { ReachableBadge } from "./ReachableBadge";
import { ThroughConnector, type RoutingConnector } from "./ThroughConnector";

export interface AssignmentReachabilityEntry {
  id: string;
  name: string;
  reachable: boolean | null;
  lastCheckedAt: string | null;
  online?: boolean;
}

/**
 * Shows which saved assignments can currently reach the resource, complementing
 * the aggregate reachability count shown by the assignment form.
 *
 * Unsaved selections are omitted because they have no reachability result yet.
 * Gateways of a resource behind a connector show the connector instead, since
 * sessions to it don't depend on the gateway reaching it directly.
 */
export function AssignmentReachability({
  label,
  assignments,
  routingConnectors = [],
}: {
  label: string;
  assignments: AssignmentReachabilityEntry[];
  routingConnectors?: RoutingConnector[];
}) {
  if (assignments.length === 0) {
    return null;
  }

  return (
    <div className="rounded-lg border border-muted bg-surface p-3">
      <span className="mb-2 block text-sm text-foreground">{label}</span>
      <ul className="space-y-1.5">
        {assignments.map((assignment) => (
          <li
            key={assignment.id}
            className="flex items-baseline justify-between gap-3 text-sm"
          >
            <span className="truncate text-foreground">{assignment.name}</span>
            {routingConnectors.length > 0 ? (
              <span className="flex shrink-0 items-baseline gap-2">
                <ThroughConnector connectors={routingConnectors} />
                <span className="text-xs text-foreground/70">—</span>
              </span>
            ) : (
              <span className="flex shrink-0 items-baseline gap-2">
                <ReachableBadge
                  reachable={assignment.reachable}
                  online={assignment.online}
                />
                <span
                  className="text-xs text-foreground/70"
                  title={
                    assignment.lastCheckedAt
                      ? new Date(assignment.lastCheckedAt).toLocaleString()
                      : undefined
                  }
                >
                  {assignment.lastCheckedAt
                    ? `checked ${formatRelativeTime(assignment.lastCheckedAt)}`
                    : "never checked"}
                </span>
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
